import { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { SatelliteTleData, SatelliteGeneration } from "../SatDataLoader/types";
import { getCurrentPositionFromTLE } from "../utils/getCurrentPositionFromTle";

const GENERATION_LABELS: Record<SatelliteGeneration, string> = {
  bluewalker3: "BlueWalker 3",
  block1: "Block 1",
  block2: "Block 2",
};

const MIN_LEO_ALT = 400;
const MAX_LEO_ALT = 700;

type CurrentPosition = { lat: number; lng: number; alt: number };

type SatPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  satellite: SatelliteTleData | null;
};

const SatPopover = ({
  open,
  anchorEl,
  onClose,
  satellite,
}: SatPopoverProps) => {
  const [currentPosition, setCurrentPosition] =
    useState<CurrentPosition | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!satellite || !open) return;

    setCurrentPosition(null);
    setLastUpdate(null);
    const updatePosition = () => {
      const position = getCurrentPositionFromTLE(satellite.tle);
      if (position) {
        setCurrentPosition(position);
        setLastUpdate(new Date());
      }
    };
    updatePosition();
    const interval = setInterval(updatePosition, 1000);
    return () => clearInterval(interval);
  }, [satellite, open]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus({ preventScroll: true });
  }, [open, anchorEl]);

  if (!satellite || !open) return null;

  const generationLabel =
    GENERATION_LABELS[satellite.info.generation ?? "block1"];
  const latitudeDirection = (currentPosition?.lat ?? 0) >= 0 ? "N" : "S";
  const longitudeDirection = (currentPosition?.lng ?? 0) >= 0 ? "E" : "W";
  const updatedTime = lastUpdate
    ? dayjs(lastUpdate).format("HH:mm:ss")
    : "Waiting…";
  const altitudePercent = currentPosition
    ? Math.min(
        100,
        Math.max(
          0,
          ((currentPosition.alt - MIN_LEO_ALT) / (MAX_LEO_ALT - MIN_LEO_ALT)) *
            100,
        ),
      )
    : 0;

  const handleClose = () => {
    onClose();
    anchorEl?.focus({ preventScroll: true });
  };

  return (
    <Paper
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="satellite-detail-title"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          handleClose();
        }
      }}
      sx={{
        position: "absolute",
        bottom: { xs: 8, md: 20 },
        left: { xs: 8, md: 20 },
        width: { xs: "calc(100% - 16px)", sm: 304 },
        maxWidth: "calc(100% - 16px)",
        maxHeight: "calc(100% - 16px)",
        overflowY: "auto",
        zIndex: 10,
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        outline: "none",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id="satellite-detail-title"
            component="h2"
            sx={{ fontSize: 14, fontWeight: 650 }}
          >
            {satellite.info.satname}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {generationLabel} · NORAD {satellite.info.satid}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close satellite details">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>
        {currentPosition && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Latitude
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontVariantNumeric: "tabular-nums",
                    mt: 0.5,
                  }}
                >
                  {Math.abs(currentPosition.lat).toFixed(2)}°{" "}
                  {latitudeDirection}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Longitude
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontVariantNumeric: "tabular-nums",
                    mt: 0.5,
                  }}
                >
                  {Math.abs(currentPosition.lng).toFixed(2)}°{" "}
                  {longitudeDirection}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Altitude
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {currentPosition.alt.toFixed(1)} km
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={altitudePercent}
              aria-label="Altitude within the 400 to 700 km reference range"
              sx={{ height: 3, borderRadius: 1, bgcolor: "action.selected" }}
            />
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}
            >
              <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                400 km
              </Typography>
              <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
                700 km
              </Typography>
            </Box>
          </>
        )}
        {!currentPosition && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Position unavailable.
          </Typography>
        )}
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
            Updated {updatedTime}
          </Typography>
          {satellite.tle && (
            <Typography sx={{ fontSize: 10, color: "primary.main" }}>
              TLE prediction
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default SatPopover;
